import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const KEY = "tf_cookie_consent";

const applyConsent = (accepted) => {
  // Hook point for any future analytics tool. Nothing is tracked without consent.
  try {
    window.dispatchEvent(new CustomEvent("cookie-consent", { detail: { accepted } }));
  } catch (e) {
    /* ignore */
  }
};

export const openCookieSettings = () => {
  window.dispatchEvent(new Event("open-cookie-settings"));
};

const CookieConsent = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const c = t.cookie;

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (!stored) {
      applyConsent(false); // opt-out until explicit consent
      setVisible(true);
    } else {
      applyConsent(stored === "accepted");
    }
    const onOpen = () => setVisible(true);
    window.addEventListener("open-cookie-settings", onOpen);
    return () => window.removeEventListener("open-cookie-settings", onOpen);
  }, []);

  const choose = useCallback((accepted) => {
    localStorage.setItem(KEY, accepted ? "accepted" : "rejected");
    applyConsent(accepted);
    setVisible(false);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-[900px] border border-white/10 bg-[#121212]/95 p-6 shadow-glow-green backdrop-blur-xl md:bottom-6 md:p-7"
          data-testid="cookie-banner"
        >
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center border border-brand-green/40 bg-brand-green/10">
                <Cookie className="h-4 w-4 text-brand-green" />
              </span>
              <div>
                <div className="font-display text-base font-bold text-white">{c.title}</div>
                <p className="mt-1 text-sm leading-relaxed text-white/60">
                  {c.text}{" "}
                  <Link
                    to="/datenschutz"
                    className="text-brand-green underline underline-offset-2 hover:text-white"
                    onClick={() => setVisible(false)}
                    data-testid="cookie-privacy-link"
                  >
                    {c.privacy}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 gap-3">
              <button
                onClick={() => choose(false)}
                className="flex-1 whitespace-nowrap border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white/80 transition-colors hover:border-white hover:text-white md:flex-none"
                data-testid="cookie-reject"
              >
                {c.reject}
              </button>
              <button
                onClick={() => choose(true)}
                className="flex-1 whitespace-nowrap bg-brand-green px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-widest text-black transition-transform hover:-translate-y-0.5 md:flex-none"
                data-testid="cookie-accept"
              >
                {c.accept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
